"use client";

import { Box, Text, HStack, type BoxProps } from "@chakra-ui/react";
import { type ReactNode } from "react";
import { LuTrendingUp, LuTrendingDown, LuMinus } from "react-icons/lu";

interface StatCardProps extends BoxProps {
  label: string;
  value: string | number;
  trend?: {
    value: number;
    type: "up" | "down" | "neutral";
  };
  change?: {
    value: number | string;
    /** Drives the arrow direction, and the colour unless `isNeutral`. */
    isPositive: boolean;
    /**
     * A move that is neither good nor bad (a market's median price rising).
     * Keeps the arrow, drops the green/red verdict.
     */
    isNeutral?: boolean;
  };
  subtitle?: string;
  icon?: ReactNode;
  /** Optional accent color for the icon tile background. */
  accentColor?: string;
}

const TREND_ICON: Record<NonNullable<StatCardProps["trend"]>["type"], ReactNode> = {
  up: <LuTrendingUp size={11} />,
  down: <LuTrendingDown size={11} />,
  neutral: <LuMinus size={11} />,
};

const TREND_COLOR: Record<NonNullable<StatCardProps["trend"]>["type"], string> = {
  up: "var(--color-success-strong)",
  down: "var(--color-error-strong)",
  neutral: "var(--color-text-tertiary)",
};

export function StatCard({
  label,
  value,
  trend,
  change,
  subtitle,
  icon,
  accentColor,
  ...props
}: StatCardProps) {
  let changeColor: string | undefined;
  let changeBg: string | undefined;
  if (change?.isNeutral) {
    changeColor = "var(--color-text-secondary)";
    changeBg = "var(--color-surface-tint)";
  } else if (change) {
    changeColor = change.isPositive
      ? "var(--color-success-strong)"
      : "var(--color-error-strong)";
    changeBg = change.isPositive
      ? "var(--color-success-light)"
      : "var(--color-error-light)";
  }

  return (
    <Box
      className="stat-card"
      position="relative"
      bg="var(--color-surface)"
      border="0.5px solid var(--color-border)"
      borderRadius="10px"
      boxShadow="0 1px 3px rgba(0,0,0,0.04)"
      px="20px"
      py="18px"
      display="flex"
      flexDirection="column"
      gap="10px"
      transition="border-color 150ms, box-shadow 150ms"
      _hover={{
        borderColor: "var(--color-border-strong)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
      }}
      {...props}
    >
      <HStack justify="space-between" align="center">
        <Text
          fontSize="11px"
          fontWeight="600"
          color="var(--color-text-tertiary)"
          textTransform="uppercase"
          letterSpacing="0.07em"
          lineHeight="1.2"
        >
          {label}
        </Text>
        {icon && (
          <Box
            w="30px"
            h="30px"
            borderRadius="7px"
            bg={accentColor || "var(--color-primary-light)"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="var(--color-primary)"
            flexShrink={0}
          >
            {icon}
          </Box>
        )}
      </HStack>

      <Text
        fontSize="28px"
        fontWeight="700"
        color="var(--color-text-primary)"
        letterSpacing="-0.03em"
        lineHeight="1"
        className="mono-number"
      >
        {value}
      </Text>

      <HStack gap="6px" align="center" minH="18px" overflow="hidden">
        {trend && (
          <HStack
            gap="1px"
            px="6px"
            py="2px"
            borderRadius="full"
            bg={`color-mix(in srgb, ${TREND_COLOR[trend.type]} 10%, transparent)`}
          >
            <Box color={TREND_COLOR[trend.type]} display="inline-flex">
              {TREND_ICON[trend.type]}
            </Box>
            <Text
              fontSize="11px"
              fontWeight="600"
              color={TREND_COLOR[trend.type]}
              className="mono-number"
            >
              {Math.abs(trend.value)}%
            </Text>
          </HStack>
        )}

        {change && (
          <Text
            as="span"
            display="inline-flex"
            alignItems="center"
            gap="1px"
            px="6px"
            py="2px"
            borderRadius="full"
            bg={changeBg}
            color={changeColor}
            fontSize="11px"
            fontWeight="600"
          >
            {change.isPositive ? "↑" : "↓"} {change.value}
          </Text>
        )}

        {subtitle && (
          <Text
            fontSize="11px"
            color="var(--color-text-tertiary)"
            lineClamp={1}
            minW={0}
            flex={1}
          >
            {subtitle}
          </Text>
        )}
      </HStack>
    </Box>
  );
}
